import { Link } from "react-router-dom";
import { Server, Globe, Zap, Shield, Layers, RefreshCw, Users, DollarSign, Lock, BarChart3, Network, Cpu } from "lucide-react";

const layers = [
  { num: "01", title: "Physical Layer", text: "Distributed hardware nodes owned by real people around the world. Your neighbour could be powering part of the internet.", color: "#ff9a4d" },
  { num: "02", title: "Network Layer", text: "Intelligent routing that finds the fastest path between any two points on Earth. Sub-50ms latency, everywhere.", color: "#4285f4" },
  { num: "03", title: "Compute Layer", text: "Serverless execution at the edge. Your code runs where your users are — no cold starts, no configuration.", color: "#34a853" },
  { num: "04", title: "Ownership Layer", text: "The Aethernet's secret weapon. Contributors own a piece of the network and earn from the traffic they power.", color: "#ffc366" },
];

const features = [
  { icon: Globe, title: "300+ Edge Locations", text: "Your code runs where your users are. Every continent, every major city." },
  { icon: Zap, title: "Sub-second Deploys", text: "Push and it's live. No pipelines, no build queues, no waiting." },
  { icon: Shield, title: "DDoS Protection", text: "Enterprise-grade security on every plan. Absorb any attack, stay online." },
  { icon: Lock, title: "Auto SSL", text: "Every deployment gets HTTPS automatically. Zero configuration." },
  { icon: RefreshCw, title: "Instant Rollbacks", text: "Something break? One click to roll back to any previous deploy." },
  { icon: BarChart3, title: "Real-time Analytics", text: "Monitor traffic, latency, and errors as they happen." },
  { icon: Layers, title: "Auto Scaling", text: "From zero to millions. Scales up and down automatically." },
  { icon: Network, title: "Global CDN", text: "Static assets cached at every edge node. Blazing fast worldwide." },
];

const ownership = [
  { icon: Users, title: "Own the Internet", text: "Run an Aethernet node and own a piece of the infrastructure. The internet, decentralized." },
  { icon: DollarSign, title: "Earn Passively", text: "Get paid for the compute and bandwidth your node contributes. Real revenue, not tokens." },
  { icon: Cpu, title: "Power the Network", text: "Every node makes the Aethernet faster and more resilient. The more people join, the better it gets." },
];

const CloudPage = ({ onOpenWaitlist }) => {
  return (
    <div className="min-h-screen bg-white" data-testid="cloud-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#fff4eb] flex items-center justify-center">
                <Server className="w-5 h-5 text-[#ff9a4d]" />
              </div>
              <span className="text-[12px] font-bold text-[#ff9a4d] tracking-wide uppercase">Eamico Cloud</span>
              <span className="text-[11px] font-semibold text-white bg-[#5f6368] px-2.5 py-0.5 rounded-full uppercase tracking-wide">Coming Soon</span>
            </div>
            <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124] leading-tight" data-testid="cloud-title">
              The world's fastest edge infrastructure — owned by the people
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5f6368] leading-relaxed max-w-2xl">
              Eamico Cloud runs on the Aethernet — our patent-pending 4-layer network where anyone can own part of the internet and earn from it. The fastest, most consistent data infrastructure worldwide.
            </p>
            <p className="mt-3 text-[13px] font-medium text-[#5f6368]">
              Launching in Closed Alpha — November 2026
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={onOpenWaitlist} className="inline-flex items-center px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm" data-testid="cloud-get-started">
                Join waitlist
              </button>
              <Link to="/enterprise" className="inline-flex items-center px-6 py-3 border border-[#dadce0] hover:bg-white text-[14px] font-medium text-[#ff9a4d] rounded-md transition-colors" data-testid="cloud-enterprise">
                Talk to sales
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Aethernet patent badge */}
      <div className="border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center gap-3 text-[13px] text-[#5f6368]">
            <div className="w-2 h-2 rounded-full bg-[#34a853]" />
            <span>Powered by the <strong className="text-[#202124]">Aethernet</strong> — technology pending patent approval</span>
          </div>
        </div>
      </div>

      {/* 4 Layers */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="max-w-2xl mb-12">
          <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-normal text-[#202124] leading-tight">
            Four layers. <span className="text-[#ff9a4d]">One unstoppable network.</span>
          </h2>
          <p className="mt-4 text-[15px] sm:text-base text-[#5f6368] leading-relaxed">
            The Aethernet is built from the ground up with a 4-layer architecture designed for speed, consistency, and shared ownership.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8" data-testid="cloud-layers">
          {layers.map((layer, i) => (
            <div key={i} className="relative p-8 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all" data-testid={`cloud-layer-${i}`}>
              <span className="text-[48px] font-extralight leading-none absolute top-6 right-8" style={{ color: `${layer.color}25` }}>
                {layer.num}
              </span>
              <div className="w-2 h-2 rounded-full mb-5" style={{ backgroundColor: layer.color }} />
              <h3 className="text-[18px] font-medium text-[#202124] mb-2">{layer.title}</h3>
              <p className="text-[14px] text-[#5f6368] leading-relaxed">{layer.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Ownership model */}
      <div className="bg-[#f8f9fa] border-t border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-2xl mb-12">
            <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-normal text-[#202124] leading-tight">
              Own the internet. <span className="text-[#ff9a4d]">Get paid for it.</span>
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#5f6368] leading-relaxed">
              The Aethernet isn't just infrastructure — it's a new model of ownership. Run a node, contribute bandwidth and compute, and earn real revenue from the traffic you power.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 lg:gap-8" data-testid="cloud-ownership">
            {ownership.map((o, i) => {
              const Icon = o.icon;
              return (
                <div key={i} className="p-8 bg-white rounded-xl border border-[#e8eaed] hover:shadow-sm transition-all" data-testid={`ownership-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#fff4eb] flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#ff9a4d]" />
                  </div>
                  <h3 className="text-[18px] font-medium text-[#202124] mb-2">{o.title}</h3>
                  <p className="text-[14px] text-[#5f6368] leading-relaxed">{o.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Infrastructure features */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="max-w-2xl mb-12">
          <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-normal text-[#202124] leading-tight">
            Infrastructure that <span className="text-[#ff9a4d]">just works</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6" data-testid="cloud-features">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="p-6 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all" data-testid={`cloud-feature-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#fff4eb] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#ff9a4d]" />
                </div>
                <h3 className="text-[15px] font-medium text-[#202124] mb-1.5">{f.title}</h3>
                <p className="text-[13px] text-[#5f6368] leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#f8f9fa] border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124]">Deploy on the Aethernet today</h2>
          <p className="mt-3 text-[15px] text-[#5f6368] max-w-md mx-auto">Start free. Scale to the edge. Own the infrastructure.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button onClick={onOpenWaitlist} className="inline-flex items-center px-6 py-3 bg-[#ff9a4d] hover:bg-[#e8872e] text-white text-[14px] font-medium rounded-md transition-colors shadow-sm">
              Join waitlist
            </button>
            <Link to="/enterprise" className="inline-flex items-center px-6 py-3 border border-[#dadce0] hover:bg-white text-[14px] font-medium text-[#ff9a4d] rounded-md transition-colors">
              Contact us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudPage;
